/**
 * PURPOSE: Inicializar la aplicación Winscore y sus proveedores globales.
 * RESPONSIBILITIES: Configurar credenciales HTTP, Redux, React Query, forzar el tema claro y montar App.
 * COLLABORATORS: App.jsx, store, axios y QueryClientProvider.
 * ROUTE: winscore/src/index.js
 */
import React from "react";
import axios from "axios";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "react-query";
import { Provider } from "react-redux";
import { store } from "./app/store";
import App from "./App";
import API_BASE_URL from "./config/config.js";
import { createCompanyContextInterceptor } from "./services/effectiveCompanyContext.js";
import {
  createAuthHeaderInterceptor,
  createUnauthorizedResponseInterceptor,
  dispatchUnauthorized,
} from "./services/authToken.js";
import "./index.css";

// FTR-0019: sin cookies de sesión; la autenticación viaja en Authorization: Bearer.
axios.defaults.withCredentials = false;
// Instancia axios por defecto (p. ej. ModalCreateEvent): misma cabecera X-Hayllis-Company que
// httpService, restringida a la API de Winscore; los hosts externos nunca la reciben (FTR-0012).
axios.interceptors.request.use(createCompanyContextInterceptor(API_BASE_URL));
// Mismo par de interceptores Bearer/401 que httpService, restringido a la API de Winscore (FTR-0019).
axios.interceptors.request.use(createAuthHeaderInterceptor(API_BASE_URL));
axios.interceptors.response.use(
  undefined,
  createUnauthorizedResponseInterceptor(API_BASE_URL, dispatchUnauthorized)
);

const ACTIVE_THEME = "light";
const documentRoot = document.documentElement;
documentRoot.classList.remove("dark", "light");
documentRoot.classList.add(ACTIVE_THEME);
documentRoot.dataset.theme = ACTIVE_THEME;
documentRoot.style.colorScheme = ACTIVE_THEME;

const queryClient = new QueryClient();

const container = document.getElementById("root");
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <App />
      </Provider>
    </QueryClientProvider>
  </React.StrictMode>
);
